import { Button, Flex, Input, Radio, Space, Alert, Spin } from "antd";
import { useState, useRef, useEffect, useCallback } from "react";
import { useCookies } from "react-cookie";
import Webcam from "react-webcam";

import frontGrid4PhotoImg from "../images/grid_4photos.png";
import frontGridAAIIImg from "../images/grid_aaii.png";
import frontGridVisionImg from "../images/grid_vision.png";
import frontGridFukudaImg from "../images/fukuda_grid.png";
import frontGridElicoidaleImg from "../images/grid_centerted.png";
import {
  EXAM_4PHOTOS_SLUGNAME,
  EXAM_AAII_SLUGNAME,
  EXAM_BALANCE_SLUGNAME,
  EXAM_MAGNO_SLUGNAME,
  EXAM_PODO_SLUGNAME,
  EXAM_PODO_STIM_SLUGNAME,
  EXAM_VISION_SLUGNAME,
  EXAM_HEAD_SLUGNAME,
  EXAM_FUKUDA_SLUGNAME,
  EXAM_ADAMS_SLUGNAME,
  EXAM_ROMBERG_SLUGNAME,
  EXAM_ANAM_SLUGNAME,
  EXAM_ELICOIDALE_SLUGNAME,
} from "./exams_definitions_utils";
import { COOKIE_WC_HEIGHT } from "./exam_const";

const getGridByExamType = (examType: string) => {
  switch (examType) {
    case EXAM_4PHOTOS_SLUGNAME:
      return frontGrid4PhotoImg;
    case EXAM_AAII_SLUGNAME:
      return frontGridAAIIImg;
    case EXAM_PODO_SLUGNAME:
      return "";
    case EXAM_PODO_STIM_SLUGNAME:
      return "";
    case EXAM_VISION_SLUGNAME:
      return frontGridVisionImg;
    case EXAM_MAGNO_SLUGNAME:
      return frontGridVisionImg;
    case EXAM_HEAD_SLUGNAME:
      return frontGridVisionImg;
    case EXAM_ADAMS_SLUGNAME:
      return frontGrid4PhotoImg;
    case EXAM_ROMBERG_SLUGNAME:
      return frontGrid4PhotoImg;
    case EXAM_FUKUDA_SLUGNAME:
      return frontGridFukudaImg;
    case EXAM_BALANCE_SLUGNAME:
      return "";
    case EXAM_ANAM_SLUGNAME:
      return "";
    case EXAM_ELICOIDALE_SLUGNAME:
      return frontGridElicoidaleImg;
    default:
      return "";
  }
};

const gridOptions = [
  { label: "Griglia", value: "grid" },
  { label: "Vuoto", value: "" },
];

const PosturalExamWebcam = ({
  takePhoto,
  posturalExamType,
  setTakePhoto,
  insertPhoto,
}: {
  takePhoto: number;
  posturalExamType: string;
  setTakePhoto: Function;
  insertPhoto: Function;
}) => {
  const [deviceId, setDeviceId] = useState<string>("");
  const [devicePodo, setDevicePodo] = useState<string>(
    "http://192.168.0.159:82/stream"
  );
  const [deviceCam, setDeviceCam] = useState<string>("");

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [isVisibleWebCam, setisVisibleWebCam] = useState(false);
  const [videoKey, setVideoKey] = useState<number>(0); // Per forzare re-render della webcam

  const [webcamStatus, setWebcamStatus] = useState<'loading' | 'ready' | 'error' | 'no-permission' | 'not-available'>('loading');
  const [webcamError, setWebcamError] = useState<string>('');
  const [useExactDeviceId, setUseExactDeviceId] = useState<boolean>(true);
  const deviceIdRef = useRef<string>('');
  const rafRef = useRef<number>(0);

  const [grid, setGrid] = useState<boolean>(true);
  const [cookies, setCookie] = useCookies([COOKIE_WC_HEIGHT]);
  const webcamRef = useRef<Webcam>(null);
  const [webcamHeight, setWebcamHeight] = useState<number>(
    cookies[COOKIE_WC_HEIGHT] ? parseInt(cookies[COOKIE_WC_HEIGHT]) : 448
  );
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null);
  const frontGridRef = useRef<HTMLImageElement>(null);

  // Stato per attivare/disattivare l'effetto specchio
  const [isMirror, setIsMirror] = useState<boolean>(
    posturalExamType === EXAM_PODO_SLUGNAME || posturalExamType === EXAM_PODO_STIM_SLUGNAME
  );
  // Utilizzo di un ref per avere sempre il valore aggiornato di isMirror
  const isMirrorRef = useRef(isMirror);
  useEffect(() => {
    isMirrorRef.current = isMirror;
  }, [isMirror]);

  // Sync deviceIdRef con deviceId
  useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);

  const handleDevices = useCallback(
    (mediaDevices: MediaDeviceInfo[]) => {
      const webcamDevices = mediaDevices.filter(
        ({ kind }: { kind: string }) => kind === "videoinput"
      );
      console.log("Dispositivi video trovati:", webcamDevices);
      
      if (webcamDevices.length > 0) {
        setDevices(webcamDevices);
        
        // Se il dispositivo corrente non è più nella lista, seleziona il primo disponibile
        const currentDeviceExists = webcamDevices.some(d => d.deviceId === deviceIdRef.current);
        if (!deviceIdRef.current || !currentDeviceExists) {
          console.log("Selezionando nuovo dispositivo:", webcamDevices[0].deviceId);
          setDeviceId(webcamDevices[0].deviceId);
        }
      } else {
        console.warn("Nessun dispositivo video trovato");
        setDevices([]);
        setDeviceId("");
        setWebcamStatus('error');
        setWebcamError('Nessuna webcam trovata. Collega una webcam e riprova.');
      }
    },
    [setDevices]
  );

  // Ricarica i dispositivi quando viene cambiato il deviceId
  const refreshDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setWebcamStatus('not-available');
        setWebcamError('La webcam richiede una connessione HTTPS sicura. Verifica che il sito sia accessibile tramite HTTPS.');
        return;
      }
      setWebcamStatus('loading');
      // Prima richiedi i permessi per tutti i dispositivi media
      await navigator.mediaDevices.getUserMedia({ video: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      handleDevices(devices);
    } catch (error: any) {
      console.error("Errore nel richiedere i dispositivi:", error);
      if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
        setWebcamStatus('no-permission');
        setWebcamError('Permesso webcam negato. Consenti l\'accesso alla fotocamera nelle impostazioni del browser e ricarica la pagina.');
      } else if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
        setWebcamStatus('error');
        setWebcamError('Nessuna webcam trovata. Collega una webcam e clicca "Aggiorna dispositivi".');
      } else if (error?.name === 'NotReadableError' || error?.name === 'TrackStartError') {
        setWebcamStatus('error');
        setWebcamError('La webcam è in uso da un\'altra applicazione. Chiudi altri programmi che utilizzano la fotocamera e riprova.');
      } else {
        setWebcamStatus('error');
        setWebcamError(`Errore webcam: ${error?.message || 'Errore sconosciuto'}. Prova a ricaricare la pagina.`);
      }
    }
  }, [handleDevices]);

  useEffect(() => {
    if (
      navigator &&
      navigator.mediaDevices
    ) {
      refreshDevices();
      
      // Ascolta per cambiamenti nei dispositivi (USB hotplug)
      const handleDeviceChange = () => {
        console.log("Cambiamento dispositivi rilevato");
        setTimeout(refreshDevices, 500); // Piccolo delay per stabilizzare
      };
      
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
      
      return () => {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      };
    } else {
      setWebcamStatus('not-available');
      setWebcamError('La webcam richiede una connessione HTTPS sicura. Verifica che il sito sia accessibile tramite HTTPS.');
    }
  }, [refreshDevices]);

  const [width, setWidth] = useState(0);
  const handleResize = () => setWidth(window.innerWidth);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    setWidth(window.innerWidth);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const rotate270: boolean =
    (width <= 640 &&
      posturalExamType !== EXAM_PODO_SLUGNAME &&
      posturalExamType !== EXAM_PODO_STIM_SLUGNAME) ||
    (width > 640 && posturalExamType === EXAM_PODO_SLUGNAME) ||
    (width > 640 && posturalExamType === EXAM_PODO_STIM_SLUGNAME);

  const useGrid: boolean =
    posturalExamType !== EXAM_PODO_SLUGNAME &&
    posturalExamType !== EXAM_PODO_STIM_SLUGNAME;

  const allRotate: boolean = posturalExamType !== EXAM_ANAM_SLUGNAME;

  useEffect(() => {
    if (hiddenCanvasRef.current && takePhoto >= 0)
      hiddenCanvasRef.current.toBlob(
        (blob: Blob | null) => {
          insertPhoto(
            hiddenCanvasRef.current ? hiddenCanvasRef.current.toDataURL() : "",
            blob,
            takePhoto
          );
          setTakePhoto(-1);
        },
        "image/png",
        1
      );
  }, [takePhoto]);

  const OpenImageWebCam = () => setisVisibleWebCam(!isVisibleWebCam);

  // requestAnimationFrame loop per rendering canvas - sostituisce setInterval
  const gridRef = useRef(grid);
  useEffect(() => {
    gridRef.current = grid;
  }, [grid]);

  useEffect(() => {
    let active = true;
    const loop = () => {
      if (!active) return;
      drawImg(gridRef.current);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      active = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, []); // Avvia una sola volta, usa refs per valori aggiornati

  // Aggiungi listener per quando il video è pronto
  useEffect(() => {
    const videoElement = webcamRef.current?.video;
    if (videoElement) {
      const handleVideoReady = () => {
        console.log("Video pronto, inizio rendering");
        drawImg(grid);
      };
      
      // Aggiungi listener per vari eventi del video
      videoElement.addEventListener('loadeddata', handleVideoReady);
      videoElement.addEventListener('play', handleVideoReady);
      videoElement.addEventListener('playing', handleVideoReady);
      
      // Se il video è già pronto, disegna subito
      if (videoElement.readyState >= 2) {
        handleVideoReady();
      }
      
      return () => {
        videoElement.removeEventListener('loadeddata', handleVideoReady);
        videoElement.removeEventListener('play', handleVideoReady);
        videoElement.removeEventListener('playing', handleVideoReady);
      };
    }
  }, [grid, deviceId, videoKey]); // Usa videoKey invece di webcamRef.current?.video

  function mirrorChange() {
    setIsMirror((prev) => !prev);
  }

  // Funzione per gestire il cambio di dispositivo
  const handleDeviceChange = (newDeviceId: string) => {
    console.log("Cambiando dispositivo da", deviceId, "a", newDeviceId);
    
    // Se c'è un flusso video attivo, fermalo prima di cambiare
    if (webcamRef.current && webcamRef.current.video && webcamRef.current.video.srcObject) {
      const stream = webcamRef.current.video.srcObject as MediaStream;
      stream.getTracks().forEach(track => {
        track.stop();
      });
    }
    
    setDeviceId(newDeviceId);
  };

  // Aggiorna il canvas ogni volta che isMirror o grid cambia
  useEffect(() => {
    drawImg(grid);
  }, [isMirror, grid]);

  // Gestione errori webcam con fallback constraint
  const handleWebcamError = useCallback((error: string | DOMException) => {
    console.error("Errore webcam:", error);
    const errorObj = typeof error === 'string' ? { name: error, message: error } : error;

    if (errorObj.name === 'OverconstrainedError' || errorObj.name === 'ConstraintNotSatisfiedError') {
      // Il constraint exact non è supportato (comune su Safari) - retry con ideal
      console.warn('Constraint exact non supportato, passaggio a ideal...');
      setUseExactDeviceId(false);
      setVideoKey(prev => prev + 1);
      return; // Non mostrare errore, stiamo facendo retry
    }

    if (errorObj.name === 'NotAllowedError' || errorObj.name === 'PermissionDeniedError') {
      setWebcamStatus('no-permission');
      setWebcamError('Permesso webcam negato. Consenti l\'accesso alla fotocamera nelle impostazioni del browser e ricarica la pagina.');
    } else if (errorObj.name === 'NotFoundError' || errorObj.name === 'DevicesNotFoundError') {
      setWebcamStatus('error');
      setWebcamError('Nessuna webcam trovata. Collega una webcam e clicca "Aggiorna dispositivi".');
    } else if (errorObj.name === 'NotReadableError' || errorObj.name === 'TrackStartError') {
      setWebcamStatus('error');
      setWebcamError('La webcam è in uso da un\'altra applicazione. Chiudi altri programmi che utilizzano la fotocamera e riprova.');
    } else {
      setWebcamStatus('error');
      setWebcamError(`Errore webcam: ${errorObj.message || 'Errore sconosciuto'}. Prova a ricaricare la pagina.`);
    }
  }, []);

  // Costruisci i videoConstraints in modo robusto per cross-browser
  const getVideoConstraints = (isMobile: boolean) => {
    const constraints: MediaTrackConstraints = {
      height: webcamHeight,
      width: Math.round(webcamHeight * 1.48),
    };
    if (deviceId) {
      constraints.deviceId = useExactDeviceId ? { exact: deviceId } : { ideal: deviceId };
    }
    if (isMobile && !deviceId) {
      constraints.facingMode = "environment";
    }
    return constraints;
  };

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (webcamRef.current?.video?.srcObject) {
        const stream = webcamRef.current.video.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Forza il re-render della webcam quando cambia il dispositivo
  useEffect(() => {
    if (deviceId) {
      console.log("Cambio dispositivo a:", deviceId);
      setVideoKey(prev => prev + 1);
      // Piccolo delay per permettere al dispositivo di inizializzarsi
      setTimeout(() => {
        drawImg(grid);
      }, 100);
    }
  }, [deviceId, grid]);

  function drawImg(drawGrid: boolean) {
    if (
      webcamRef.current &&
      canvasRef.current &&
      hiddenCanvasRef.current &&
      frontGridRef.current
    ) {
      const video: Webcam = webcamRef.current;
      const canvas: HTMLCanvasElement = canvasRef.current;
      const hiddenCanvas: HTMLCanvasElement = hiddenCanvasRef.current;
      const frontGrid: HTMLImageElement = frontGridRef.current;

      if (video != null && canvas != null && hiddenCanvas) {
        const ctx = canvas.getContext("2d");
        const hiddenCtx = hiddenCanvas.getContext("2d");

        // Verifica che il video sia pronto (readyState >= 2 significa che i dati sono disponibili)
        if (video.video != null && ctx != null && hiddenCtx != null && video.video.readyState >= 2) {
          // Imposta le dimensioni dei canvas in base a rotate270
          canvas.height = rotate270
            ? video.video.videoWidth
            : video.video.videoHeight;
          canvas.width = rotate270
            ? video.video.videoHeight
            : video.video.videoWidth;

          hiddenCanvas.width = rotate270
            ? video.video.videoHeight
            : video.video.videoWidth;
          hiddenCanvas.height = rotate270
            ? video.video.videoWidth
            : video.video.videoHeight;

          // Pulisce i canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          hiddenCtx.clearRect(0, 0, hiddenCanvas.width, hiddenCanvas.height);

          if (rotate270) {
            // Salva lo stato del contesto per poterlo ripristinare
            ctx.save();
            hiddenCtx.save();

            // Applica prima la traslazione e la rotazione (equivalente a 270° o -90°)
            ctx.translate(0, canvas.height);
            ctx.rotate(-90 * Math.PI / 180);
            hiddenCtx.translate(0, hiddenCanvas.height);
            hiddenCtx.rotate(-90 * Math.PI / 180);

            // Se il mirror è abilitato (prendendo il valore dal ref aggiornato)
            if (isMirrorRef.current) {
              ctx.translate(canvas.width, 0);
              ctx.scale(-1, 1);

              hiddenCtx.translate(hiddenCanvas.width, 0);
              hiddenCtx.scale(-1, 1);
            }

            // Disegna il video
            ctx.drawImage(video.video, 0, 0, canvas.height, canvas.width);
            hiddenCtx.drawImage(video.video, 0, 0, hiddenCanvas.height, hiddenCanvas.width);

            // Ripristina il contesto
            ctx.restore();
            hiddenCtx.restore();

            // Disegna anche la griglia se richiesta, applicando le stesse trasformazioni
            if (frontGrid != null && drawGrid) {
              ctx.save();
              ctx.translate(0, canvas.height);
              ctx.rotate(-90 * Math.PI / 180);
              if (isMirrorRef.current) {
                ctx.translate(canvas.width, 0);
                ctx.scale(-1, 1);
              }
              ctx.drawImage(frontGrid, 0, 0, canvas.height, canvas.width);
              ctx.restore();
            }
          } else {
            // Ramo senza rotazione a 270°
            if (isMirrorRef.current) {
              ctx.translate(canvas.width, 0);
              ctx.scale(-1, 1);
              ctx.drawImage(video.video, 0, 0, canvas.width, canvas.height);

              hiddenCtx.translate(hiddenCanvas.width, 0);
              hiddenCtx.scale(-1, 1);
              hiddenCtx.drawImage(video.video, 0, 0, hiddenCanvas.width, hiddenCanvas.height);
            } else {
              ctx.drawImage(video.video, 0, 0, canvas.width, canvas.height);
              hiddenCtx.drawImage(video.video, 0, 0, hiddenCanvas.width, hiddenCanvas.height);
            }

            // Disegna la griglia se richiesta
            if (frontGrid != null && drawGrid) {
              ctx.drawImage(frontGrid, 0, 0, canvas.width, canvas.height);
            }
          }
        }
      }
    }
  }

  return (
    <>
      {/* Messaggi di errore/stato webcam */}
      {webcamStatus === 'not-available' && (
        <Alert
          message="Webcam non disponibile"
          description={webcamError}
          type="error"
          showIcon
          style={{ margin: '16px' }}
        />
      )}
      {webcamStatus === 'no-permission' && (
        <Alert
          message="Permesso fotocamera negato"
          description={webcamError}
          type="warning"
          showIcon
          style={{ margin: '16px' }}
          action={
            <Button size="small" onClick={() => window.location.reload()}>
              Ricarica pagina
            </Button>
          }
        />
      )}
      {webcamStatus === 'error' && (
        <Alert
          message="Errore webcam"
          description={webcamError}
          type="error"
          showIcon
          style={{ margin: '16px' }}
          action={
            <Button size="small" onClick={refreshDevices}>
              Riprova
            </Button>
          }
        />
      )}
      {webcamStatus === 'loading' && (
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '12px', color: '#666' }}>Inizializzazione webcam...</p>
        </div>
      )}

      {width > 640 && (
        // Big Screen Mode
        <>
          <div style={{ zoom: "1" }}>
            <Flex justify={"space-evenly"} align={"center"}>
              <Flex align="center">
              <div style={{ paddingLeft: "100px" }}></div>
                <Space size="small">
                  <div >
                  <label>Webcam selezionata: </label> 
                  <select
                    value={deviceId}
                    onChange={(e) => handleDeviceChange(e.target.value)}
                    style={{ marginRight: "10px" }}
                  >
                    {devices.map((d, index) => (
                      <option key={`${d.deviceId}-${index}`} value={d.deviceId}>
                        {d.label || `Camera ${index + 1}`}
                      </option>
                    ))}
                  </select>
                  <Button 
                    size="small" 
                    onClick={refreshDevices}
                    title="Ricarica dispositivi"
                    style={{ marginLeft: "5px" }}
                  >
                    Aggiorna dispositivi 🔄
                  </Button>
                  </div>
                </Space>
              </Flex>
              <Flex align="center">
                <Space size="small">
                  <div>
                  <label>Dimensione immagine: </label>
                  <Input
                    className="webbo"
                    defaultValue={webcamHeight}
                    aria-label="Dimensione immagine"
                    type="range"
                    min={400}
                    step={50}
                    max={1200}
                    onChange={(e) => {
                      setWebcamHeight(parseInt(e.target.value));
                      setCookie(COOKIE_WC_HEIGHT, e.target.value, {
                        path: "/",
                        expires: new Date(
                          new Date().getTime() + 1000 * 60 * 60 * 24 * 365 * 5
                        ),
                      });
                    }}
                  />
                  </div>
                </Space>
              </Flex>
            </Flex>
          </div>
          <div className="centered">
            <img
              ref={frontGridRef}
              style={{ display: "none" }}
              src={getGridByExamType(posturalExamType)}
            />
            <Webcam
              key={`webcam-${videoKey}-${deviceId}`}
              audio={false}
              ref={webcamRef}
              screenshotQuality={1.0}
              mirrored={false}
              playsInline={true}
              forceScreenshotSourceSize={true}
              onUserMedia={() => {
                console.log("Webcam caricata con successo");
                setWebcamStatus('ready');
                setWebcamError('');
                drawImg(grid);
              }}
              onUserMediaError={handleWebcamError}
              videoConstraints={getVideoConstraints(false)}
              style={{
                width: "0%",
                height: "0%",
              }}
            />
            <canvas className="webb" ref={canvasRef} />
            <canvas ref={hiddenCanvasRef} style={{ display: "none" }} />
          </div>

          <div className="centered" style={{ width: "50%" }}>
            {useGrid && (
              <Radio.Group
                className="exam-image-btn"
                options={gridOptions}
                onChange={(e) => setGrid(e.target.value == "grid")}
                defaultValue={"grid"}
                optionType="button"
                buttonStyle="solid"
              />
            )}

            {(posturalExamType === EXAM_PODO_SLUGNAME ||
              posturalExamType === EXAM_PODO_STIM_SLUGNAME) && (
              <Button onClick={mirrorChange}>Inverti</Button>
            )}
          </div>
        </>
      )}

      {width <= 640 && (
        // Mobile Mode
        <>
          <Button
            onClick={OpenImageWebCam}
            style={{ width: "50%", margin: "auto", marginBottom: "10px" }}
          >
            Posizionamento
          </Button>

          {isVisibleWebCam && (
            <>
              <div>
                <div style={{ display: "none" }}>
                  <select
                    value={deviceId}
                    onChange={(e) => handleDeviceChange(e.target.value)}
                    style={{ marginRight: "16px" }}
                  >
                    {devices.map((d, index) => (
                      <option key={`${d.deviceId}-${index}`} value={d.deviceId}>
                        {d.label || `Camera ${index + 1}`}
                      </option>
                    ))}
                  </select>
                  {useGrid && (
                    <Radio.Group
                      className="exam-image-btn"
                      options={gridOptions}
                      onChange={(e) => setGrid(e.target.value == "grid")}
                      defaultValue={"grid"}
                      optionType="button"
                      buttonStyle="solid"
                    />
                  )}
                </div>
                <div className="centered" style={{ width: "50%" }}>
                  <div className="webbolos">
                    <Input
                      style={{ display: "none" }}
                      className="webbo"
                      defaultValue={webcamHeight}
                      type="range"
                      min={500}
                      step={50}
                      max={1200}
                      onChange={(e) => {
                        setWebcamHeight(parseInt(e.target.value));
                        setCookie(COOKIE_WC_HEIGHT, e.target.value, {
                          path: "/",
                          expires: new Date(
                            new Date().getTime() + 1000 * 60 * 60 * 24 * 365 * 5
                          ),
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="centered">
                <img
                  ref={frontGridRef}
                  style={{ display: "none" }}
                  src={getGridByExamType(posturalExamType)}
                />
                <Webcam
                  key={`webcam-mobile-${videoKey}-${deviceId}`}
                  audio={false}
                  ref={webcamRef}
                  screenshotQuality={1.0}
                  mirrored={false}
                  playsInline={true}
                  forceScreenshotSourceSize={true}
                  onUserMedia={() => {
                    console.log("Webcam mobile caricata con successo");
                    setWebcamStatus('ready');
                    setWebcamError('');
                    drawImg(grid);
                  }}
                  onUserMediaError={handleWebcamError}
                  videoConstraints={getVideoConstraints(true)}
                  style={{
                    width: "0%",
                    height: "0%",
                  }}
                />
                <canvas
                  style={{
                    position: "fixed",
                    width: "100vw",
                    zIndex: "99999999",
                    marginBottom: "25px",
                    left: "0",
                  }}
                  ref={canvasRef}
                />
                <canvas
                  ref={hiddenCanvasRef}
                  style={{
                    display: "none",
                  }}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default PosturalExamWebcam;
